import {z} from 'zod';

import {IsoUtcDateTimeSchema} from './common.js';

/**
 * Contratos da entrada automática de notícias.
 *
 * A regra que organiza este arquivo é a mesma da geração: **o que vem de fora é
 * DADO, nunca instrução**. Um feed é um documento que um terceiro escreve, e
 * quem o escreve pode pôr lá dentro qualquer coisa, inclusive texto redigido
 * para um modelo de linguagem ler. Por isso nada que sai de um feed chega
 * inteiro a lugar nenhum: título vem cortado e sem marcação, endereço tem que
 * cair no host oficial declarado no arquivo, e data tem que ser data.
 *
 * O que este arquivo NÃO tem, de propósito: campo para o corpo da matéria.
 * Resumo e premissa da pauta são escritos por nós, com o título já sanitizado
 * dentro. Trazer o corpo seria abrir a porta que a esteira de texto fechou.
 */

// ---------------------------------------------------------------------------
// A lista curada de fontes
// ---------------------------------------------------------------------------

/** Só https. `http` viaja em claro, e o resto não é fonte, é vetor. */
const HttpsUrlSchema = z
  .url()
  .max(2_000)
  .refine((valor) => valor.startsWith('https://'), {
    message: 'a fonte de notícia precisa ser https',
  });

/**
 * Um host, sem esquema, sem porta e sem caminho.
 *
 * Ele é a cerca do item: um item do feed cujo link não caia neste host, ou num
 * subdomínio dele, é descartado. Feed é conteúdo de fora, e um item plantado
 * apontando para outro lugar é a maneira mais barata de pôr link de terceiro na
 * conta do dono.
 */
const HostSchema = z
  .string()
  .trim()
  .min(4)
  .max(253)
  .regex(
    /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/u,
    'host em minúsculas, sem esquema, sem porta e sem caminho',
  );

export const NewsFeedSchema = z.strictObject({
  /** Identificador curto e estável. Vira tag da pauta e aparece no relatório. */
  chave: z
    .string()
    .trim()
    .regex(/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/u, 'chave em minúsculas e hífen'),
  /** Quem publica, escrito por nós. Vai para `sources.author` e para a legenda. */
  publisher: z.string().trim().min(2).max(120),
  /** O endereço do feed RSS ou Atom. */
  feedUrl: HttpsUrlSchema,
  /**
   * O host OFICIAL das matérias daquele veículo.
   *
   * Separado de `feedUrl` porque muita gente serve o feed de um subdomínio e as
   * matérias de outro. Declarar os dois é o que permite a cerca ser exata em
   * vez de "o mesmo domínio, mais ou menos".
   */
  articleHost: HostSchema,
});
export type NewsFeed = z.infer<typeof NewsFeedSchema>;

export const NewsFeedFileSchema = z.strictObject({
  /**
   * A explicação fica DENTRO do arquivo.
   *
   * JSON não tem comentário, e um arquivo de política sem o motivo escrito é o
   * que alguém edita achando que é lista de links.
   */
  sobre: z.string().trim().min(20).max(2_000),
  feeds: z.array(NewsFeedSchema).min(1).max(40),
});
export type NewsFeedFile = z.infer<typeof NewsFeedFileSchema>;

// ---------------------------------------------------------------------------
// A entrada, item a item
// ---------------------------------------------------------------------------

/**
 * Um item do feed, já sanitizado e conferido.
 *
 * Ele nasce do extrator e é o ÚNICO formato que o serviço aceita. O extrator
 * devolve o que conseguiu ler; o schema decide o que é aproveitável.
 */
export const NewsEntrySchema = z.strictObject({
  /** Sem marcação, sem caractere de controle, com espaço colapsado. */
  title: z.string().trim().min(8).max(180),
  url: HttpsUrlSchema,
  /** Obrigatória. Notícia sem data não é notícia recente, é notícia sem data. */
  publishedAt: IsoUtcDateTimeSchema,
});
export type NewsEntry = z.infer<typeof NewsEntrySchema>;

/**
 * Por que um item NÃO virou pauta.
 *
 * Enum fechado, e não texto livre, pelo mesmo motivo dos códigos dos portões: o
 * relatório precisa responder "quantos itens caíram por estarem velhos?" seis
 * meses depois, e campo de texto não responde isso.
 */
export const NEWS_SKIP_REASONS = [
  'FEED_UNREACHABLE',
  /**
   * O item não passou no FORMATO, e não foi só a data que faltou.
   *
   * Este código já foi o balde de tudo que falhava na gravação, e foi por isso
   * que duas notícias da OpenAI caíram em produção em 2026-09-05 sem ninguém
   * conseguir dizer por quê. Hoje ele descreve só o que o nome diz: quem recusa
   * por formato é o extrator, ANTES da gravação, e a recusa da gravação é
   * `ENTRY_REFUSED`.
   *
   * A esteira o emite repassando o descarte do extrator, uma linha por bloco.
   * Ele já foi um código morto, escrito no enum e emitido por ninguém, e nesse
   * período um feed que servia vinte itens e entregava zero era indistinguível
   * de um feed sem novidade.
   */
  'ENTRY_MALFORMED',
  /**
   * Uma cerca do servidor recusou a gravação daquele item.
   *
   * QUAL cerca vai no `codigo` da linha, e é a diferença entre "o item não
   * entrou" e "a captura da prova levou 403 do site". As causas são muitas
   * (evidência inalcançável, endereço privado, corpo grande demais, redirecionamento
   * sem destino) e todas nascem de uma `ApiProblem` nossa.
   */
  'ENTRY_REFUSED',
  'ENTRY_OFF_HOST',
  /**
   * O bloco tinha título e endereço válidos, e nenhuma data aproveitável.
   *
   * "Nenhuma data aproveitável" cobre os DOIS casos: a tag de data ausente, e a
   * tag presente com um texto que não vira instante. `dataDoItem` devolve nulo
   * nos dois, e nos dois o conserto é o mesmo, porque data que não se lê não
   * é data.
   *
   * Ele é separado de `ENTRY_MALFORMED` porque a causa é outra e o conserto é
   * outro: título com marcação é item plantado, e feed inteiro sem data é
   * endereço errado na lista curada. `developers.googleblog.com` serve os dois
   * caminhos de feed com vinte `<item>` sem `pubDate`, `published`, `dc:date`
   * nem `updated`, medido em 2026-09-05, e é o caso que batizou este código.
   *
   * Notícia sem data não é notícia recente, é notícia sem data: `publishedAt` é
   * obrigatório em `NewsEntrySchema` de propósito, e a régua de idade não tem
   * como existir sem ele.
   */
  'ENTRY_UNDATED',
  'ENTRY_STALE',
  'ENTRY_OFF_TOPIC',
  'ENTRY_ALREADY_KNOWN',
  'DAILY_LIMIT_REACHED',
] as const;
export const NewsSkipReasonSchema = z.enum(NEWS_SKIP_REASONS);
export type NewsSkipReason = z.infer<typeof NewsSkipReasonSchema>;

/**
 * A FORMA de um código de recusa, e não a lista deles.
 *
 * O `motivo` é enum fechado porque ele é a CLASSE, e classe nova é decisão de
 * quem escreve a esteira. O `codigo` é a CAUSA, e ela vem do catálogo de
 * `ApiProblem` do servidor, que cresce em dez arquivos diferentes: repetir esse
 * catálogo aqui seria a lista escrita à mão que fica para trás no dia em que
 * uma cerca nova nascer, e a recusa nova apareceria como nula.
 *
 * Então a cerca aqui é a forma, e ela continua sendo uma lista de PERMISSÃO:
 * maiúsculas, dígitos e sublinhado, e nada mais. Um código que não caiba nela é
 * recusado pelo formato esperado em vez de ser limpo, que é o mesmo que o
 * schema faz com título de feed.
 */
export const NewsRefusalCodeSchema = z
  .string()
  .regex(
    /^[A-Z][A-Z0-9_]{2,63}$/u,
    'código de recusa em MAIÚSCULAS, dígitos e sublinhado',
  );

export const NewsIntakeLineSchema = z.strictObject({
  feed: z.string().min(1),
  /** Ausente quando a recusa foi do feed inteiro. */
  url: z.string().nullable(),
  title: z.string().nullable(),
  publishedAt: IsoUtcDateTimeSchema.nullable(),
  desfecho: z.enum(['criada', 'recusada']),
  motivo: NewsSkipReasonSchema.nullable(),
  /**
   * A cerca que recusou, quando o `motivo` sozinho não diz qual foi.
   *
   * Nulo em toda linha cuja classe já é a causa: `ENTRY_STALE` não tem mais o
   * que explicar. Preenchido em `ENTRY_REFUSED`, onde ele é a única coisa que
   * separa um 403 do veículo de um endereço privado bloqueado.
   */
  codigo: NewsRefusalCodeSchema.nullable(),
  /** `topics.id`, quando a pauta nasceu de verdade. Nulo no ensaio. */
  topicId: z.uuidv4().nullable(),
});
export type NewsIntakeLineDto = z.infer<typeof NewsIntakeLineSchema>;

export const NewsIntakeReportSchema = z.strictObject({
  aplicou: z.boolean(),
  asOf: IsoUtcDateTimeSchema,
  feedsLidos: z.int().nonnegative(),
  feedsFalhos: z.int().nonnegative(),
  criadas: z.int().nonnegative(),
  recusadas: z.int().nonnegative(),
  limite: z.int().positive(),
  linhas: z.array(NewsIntakeLineSchema),
});
export type NewsIntakeReportDto = z.infer<typeof NewsIntakeReportSchema>;

/** O corpo que a porta interna aceita. Estrito: campo a mais é 422. */
export const NewsIntakeRequestSchema = z.strictObject({
  asOf: IsoUtcDateTimeSchema.optional(),
});
export type NewsIntakeRequest = z.infer<typeof NewsIntakeRequestSchema>;
